import { ColDef, ValueFormatterParams } from 'ag-grid-community';

import { computed, inject } from '@angular/core';

import { ResourcesService } from '@mitsubishi/core';
import { SpinnerService } from '@mitsubishi/ui/spinner';
import { ApmGridColumnType } from '@mitsubishi/ui/table';
import { getColorWithOpacity } from '@mitsubishi/utils';

import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { Preset, SystemUsagePlanPresetRequest } from '../../models/energy-analysis-preset.model';
import { EnergyAnalysisRule, EnergyAnalysisRulesRequest } from '../../models/energy-analysis-rules.model';
import {
  System,
  SystemRequest,
  SystemResponse,
  SystemUnit,
  SystemUnitRequest,
  UpdateSystemRequest,
  UpdateSystemUnitRequest,
  UpdateSystemsComparisonSettingsRequest,
  systemsComparisonSectionSettings,
} from '../../models/energy-analysis-system.model';
import {
  GroupComparisonInfo,
  PaybackInfo,
  PerformanceData,
  SystemSvcResultField,
  SystemsTotals,
  Task,
} from '../../models/energy-analysis-task.model';
import {
  CapacityCurveData,
  CapacityCurveLabel,
  ClimateDataRequest,
  EnergyAnalysesList,
  EnergyAnalysis,
  EnergyAnalysisConfig,
  EnergyAnalysisFilters,
  EnergyAnalysisListRequest,
  EnergyAnalysisSorting,
  SeasonChartData,
  SystemUsagePlanBinsTableColumn,
  SystemUsagePlanHistogramData,
  UpdateEnergyAnalysisRequest,
  UpdateManualBinsTable,
  cityChartData,
} from '../../models/energy-analysis.model';
import { EnergyAnalysisService } from '../../services/energy-analysis.service';

export interface EnergyAnalisyListStateModel {
  paginatedData: Partial<EnergyAnalysesList>;
  currentFilters: EnergyAnalysisFilters;
  currentSorting: EnergyAnalysisSorting;
  currentPage: number;
  pageSize: number;
  activeEnergyAnalysis: EnergyAnalysis | null;
  activeEnergyAnalysisConfig: EnergyAnalysisConfig | null;
  economicParameters: UpdateEnergyAnalysisRequest | null;
  allowSaveEconomicParameters: boolean;
  rowsManualBinsTable: { [key: string]: number }[];
  allowSaveManualBinsTable: boolean;
  cityClimateDataParams: ClimateDataRequest;
  rules: EnergyAnalysisRule[];
  presets: Preset[];
  systems: SystemResponse;
  activeSystem: System | null;
  systemUnits: SystemUnit[];
  totalSystemUnits: number;
  activeSystemUnit: SystemUnit | null;
  systemUsagePlanGrid: any;
  systemUsagePlanHistogram: SystemUsagePlanHistogramData[];
  systemUsagePlanBinsData: { [key: string]: number }[];
  systemUsagePlanBinsColumnsDefinitions: SystemUsagePlanBinsTableColumn[];
  capacityCurveData: CapacityCurveData[];
  capacityCurveDataLabels: CapacityCurveLabel | null;
  cityClimateData: cityChartData[];
  activeTaskPerformanceCol: any[];
  taskPerformanceFields: SystemSvcResultField | null;
  tasksPerformanceData: PerformanceData[];
  taskPerformanceColDef: ColDef[];
  energyAnalysisPaybackFields: any[];
  energyAnalysisPaybackData: { [key: string]: number | string | null }[];
  energyAnalysisPaybackInfo: PaybackInfo | null;
  energyAnalysisGroupComparisonColumns: {
    valueYField: string;
    categoryXField: string;
    label?: string;
    color?: string;
  }[];
  energyAnalysisGroupComparisonData: { [key: string]: number | string | null }[];
  energyAnalysisGroupComparisonInfo: GroupComparisonInfo | null;
  systemsTotalsFields: {
    field: number;
    idResource: number;
  }[];
  systemsTotals: SystemsTotals[];
  // to remove with socket
  activeTaskStatus: Task | null;
  svcRequests: { svcRequest: any; systemId: string }[];
  systemSvcResultCurves: {
    systemName: string;
    curves: any[];
  }[];
  invalidatedTasks: boolean;
  systemWorkingModes: { id: string; name: string }[];
  colors: string[];
  systemsComparisonSettings: systemsComparisonSectionSettings[];
}

const initialState: EnergyAnalisyListStateModel = {
  paginatedData: {},
  currentFilters: {},
  currentSorting: {},
  currentPage: 1,
  pageSize: 100,
  activeEnergyAnalysis: null,
  activeEnergyAnalysisConfig: null,
  economicParameters: null,
  allowSaveEconomicParameters: false,
  rowsManualBinsTable: [],
  allowSaveManualBinsTable: false,
  cityClimateDataParams: {
    type: 'ANNUAL_HOURLY_TEMPERATURE',
    contentRepresentation: 'HISTOGRAM',
    binSize: 1,
  },
  rules: [],
  presets: [],
  systems: {
    elements: [],
    totalElements: 0,
    totalPages: 0,
  },
  systemUnits: [],
  totalSystemUnits: 0,
  activeSystem: null,
  activeSystemUnit: null,
  systemUsagePlanGrid: [],
  systemUsagePlanHistogram: [],
  systemUsagePlanBinsData: [],
  systemUsagePlanBinsColumnsDefinitions: [],
  capacityCurveData: [],
  capacityCurveDataLabels: null,
  cityClimateData: [],
  activeTaskPerformanceCol: [],
  taskPerformanceFields: null,
  tasksPerformanceData: [],
  taskPerformanceColDef: [],
  energyAnalysisPaybackFields: [],
  energyAnalysisPaybackData: [],
  energyAnalysisPaybackInfo: null,
  energyAnalysisGroupComparisonColumns: [],
  energyAnalysisGroupComparisonData: [],
  energyAnalysisGroupComparisonInfo: null,
  systemsTotalsFields: [],
  systemsTotals: [],
  activeTaskStatus: null,
  svcRequests: [],
  systemSvcResultCurves: [],
  invalidatedTasks: false,
  systemWorkingModes: [],
  colors: ['#F8BF75', '#C5AFAF', '#A3C6D4', '#B2D6A7', '#F2A6A6'],
  systemsComparisonSettings: [],
};

export const EnergyAnalisesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store, resourcesService = inject(ResourcesService)) => ({
    energyAnalysisRequestParameters: computed<EnergyAnalysisListRequest>(() => {
      const filters = store.currentFilters();
      const sorting = store.currentSorting();
      return {
        ...filters,
        ...sorting,
        page: store.currentPage(),
        pageSize: store.pageSize(),
      };
    }),
    energyAnalysisRulesRequestParameters: computed<EnergyAnalysisRulesRequest | null>(() => {
      if (!store.activeEnergyAnalysis()) return null;
      return {
        page: 1,
        pageSize: 80,
        orderBy: 'CREATED_AT',
        orderDirection: 'DESC',
        analysesIds: [store.activeEnergyAnalysis()!.id],
      };
    }),
    presetRequestParameters: computed<SystemUsagePlanPresetRequest>(() => ({
      page: 1,
      pageSize: 80,
      orderBy: 'CREATED_AT',
      orderDirection: 'DESC',
      analysisId: store.activeEnergyAnalysis()?.id,
    })),
    seasonChartData: computed<SeasonChartData[]>(() => {
      interface SeasonRanges {
        startRange1: number | null;
        endRange1: number | null;
        startRange2?: number | null;
        endRange2?: number | null;
      }

      const SEASON_MAP = {
        COOLING: {
          label: resourcesService.getTextResource('451641'),
          color: '#A1DAE8',
        },
        HEATING: {
          label: resourcesService.getTextResource('451637'),
          color: '#FFA1A8',
        },
      };

      function isMainSeasonCrossingYearEnd(altSeasonStart: string | null, altSeasonEnd: string | null): boolean {
        if (altSeasonStart && altSeasonEnd) {
          const altStart = createDate(altSeasonStart);
          const altEnd = createDate(altSeasonEnd);
          return altStart > altEnd;
        } else {
          return false;
        }
      }

      function createDate(date: string): number {
        const dateParts = date.substring(2).split('-');
        const month = parseInt(dateParts[0]) - 1;
        const day = parseInt(dateParts[1]);
        return new Date(2018, month, day).getTime();
      }

      function calculateCrossYearRanges(altSeasonStart: string, altSeasonEnd: string): SeasonRanges {
        const yearStart = new Date(2018, 0, 1).getTime(); // 1 gennaio
        const yearEnd = new Date(2018, 11, 31).getTime(); // 31 dicembre

        const altStart = createDate(altSeasonStart);
        const altEnd = createDate(altSeasonEnd);

        // Range 1: da inizio anno al giorno prima di altStart
        // Range 2: dal giorno dopo altEnd a fine anno
        const range1End = altStart - 24 * 60 * 60 * 1000;
        const range2Start = altEnd + 24 * 60 * 60 * 1000;

        return {
          startRange1: yearStart,
          endRange1: range1End,
          startRange2: range2Start,
          endRange2: yearEnd,
        };
      }

      const data: SeasonChartData[] = [];
      if (store.activeEnergyAnalysis() && store.activeEnergyAnalysis()!.defaultSeason) {
        const mainSeason = store.activeEnergyAnalysis()!.defaultSeason!;
        const alternativeSeason = mainSeason === 'HEATING' ? 'COOLING' : 'HEATING';

        // definition of alternative season range
        let alternativeSeasonStart = store.activeEnergyAnalysis()!.altSeasonStart;
        let alternativeSeasonEnd = store.activeEnergyAnalysis()!.altSeasonEnd;

        // definition of main season range
        let mainRanges: SeasonRanges | null = null;

        let alternativeRanges: SeasonRanges | null = null;

        // is empty alternative season
        if (!alternativeSeasonStart || !alternativeSeasonEnd) {
          mainRanges = {
            startRange1: createDate('--01-01'),
            endRange1: createDate('--12-31'),
          };
          alternativeRanges = {
            startRange1: null,
            endRange1: null,
          };
        } else {
          if (isMainSeasonCrossingYearEnd(alternativeSeasonStart, alternativeSeasonEnd)) {
            alternativeRanges = calculateCrossYearRanges(
              store.activeEnergyAnalysis()!.altSeasonEnd!,
              store.activeEnergyAnalysis()!.altSeasonStart!
            );
            mainRanges = {
              startRange1: createDate(alternativeSeasonStart!),
              endRange1: createDate(alternativeSeasonEnd!),
            };
          } else {
            mainRanges = calculateCrossYearRanges(
              store.activeEnergyAnalysis()!.altSeasonStart!,
              store.activeEnergyAnalysis()!.altSeasonEnd!
            );
            alternativeRanges = {
              startRange1: createDate(alternativeSeasonStart!),
              endRange1: createDate(alternativeSeasonEnd!),
            };
          }
        }

        // main season row
        const mainSeasonData: SeasonChartData = {
          season: SEASON_MAP[mainSeason].label,
          columnSettings: {
            stroke: SEASON_MAP[mainSeason].color,
            fill: SEASON_MAP[mainSeason].color,
          },
          ...mainRanges,
        };
        data.push(mainSeasonData);

        // alternative season row
        const alternativeSeasonData: SeasonChartData = {
          season: SEASON_MAP[alternativeSeason].label,
          columnSettings: {
            stroke: SEASON_MAP[alternativeSeason].color,
            fill: SEASON_MAP[alternativeSeason].color,
          },
          ...alternativeRanges,
        };
        data.push(alternativeSeasonData);

        // always put cooling season at top
        data.sort((a, b) => {
          if (a.season === SEASON_MAP.COOLING.label) return 1;
          if (b.season === SEASON_MAP.COOLING.label) return -1;
          return 0;
        });
      }

      return data;
    }),
    systemRequestParameters: computed<SystemRequest | null>(() => {
      if (!store.activeEnergyAnalysis()) return null;
      return {
        page: 1,
        pageSize: 80,
        orderBy: 'CREATED_AT',
        orderDirection: 'DESC',
        analysesIds: [store.activeEnergyAnalysis()!.id],
      };
    }),
    systemUnitRequestParameters: computed<SystemUnitRequest | null>(() => {
      if (!store.activeSystem()) return null;
      return {
        page: 1,
        pageSize: 80,
        orderBy: 'CREATED_AT',
        orderDirection: 'DESC',
        systemsIds: [store.activeSystem()!.id],
      };
    }),

    mainHistogramData: computed(() => {
      const mergeByTemperature = (...arrays: any[]) => {
        const map = new Map();

        for (const arr of arrays) {
          for (const obj of arr) {
            const key = obj.temperature;
            if (!map.has(key)) {
              map.set(key, { temperature: key });
            }
            Object.assign(map.get(key), obj);
          }
        }
        const list = Array.from(map.values()).sort((a, b) => a.temperature - b.temperature);
        return list;
      };

      const histogram = [...store.systemUsagePlanHistogram()];
      let background = [...store.cityClimateData()];
      const linear =
        store.systemUsagePlanBinsData()?.map(row => {
          return {
            temperature: row.temperature,
            coolingPower: row.cooling_capacity_ratio * 100,
            thermalPower: row.heating_capacity_ratio * 100,
          };
        }) || [];

      // add backgroud points only for existing temperature in histogram
      background = background.filter(dataPoint => {
        const existingTemperatre = linear.find(lp => lp.temperature === dataPoint.temperature);
        return existingTemperatre !== undefined;
      });

      const mainChart = mergeByTemperature(background, histogram, linear);
      return mainChart;
    }),

    selectedSystemsIds: computed<string[]>(() => {
      const selectedIds: string[] = [];
      store.systemsComparisonSettings().forEach(setting => {
        if (setting.isSelected) {
          selectedIds.push(setting.systemId);
        }
      });
      return selectedIds;
    }),
  })),
  withMethods(
    (
      store,
      energyAnalysesService = inject(EnergyAnalysisService),
      spinnerService = inject(SpinnerService),
      resourcesService = inject(ResourcesService)
    ) => {
      const loadEnergyAnalyses = () => {
        spinnerService.showMainSpinner();
        energyAnalysesService.searchEnergyAnalyses(store.energyAnalysisRequestParameters()).subscribe({
          next: response => {
            spinnerService.hideMainSpinner();

            patchState(store, {
              paginatedData: response,
            });
          },
          error: error => {
            spinnerService.hideMainSpinner();
            console.error('Error loading energy analyses list', error);
          },
        });
      };

      const updateEnergyAnalysisFilters = (filters: EnergyAnalysisFilters) => {
        patchState(store, { currentFilters: filters });
      };

      const getEnergyAnalysis = (energyAnalysisId: string) => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.getEnergyAnalysis(energyAnalysisId).subscribe({
            next: response => {
              patchState(store, {
                activeEnergyAnalysis: response as EnergyAnalysis,
              });
              resolve();
            },
            error: error => {
              console.error('Error loading energy analyses list', error);
              reject(error);
            },
          });
        });
      };

      const getEnergyAnalysisConfig = (energyAnalysisId: string) => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.getEnergyAnalysisConfig(energyAnalysisId).subscribe({
            next: response => {
              patchState(store, {
                activeEnergyAnalysisConfig: response as EnergyAnalysisConfig,
              });
              resolve();
            },
            error: error => {
              console.error('Error loading energy analyses config', error);
              reject(error);
            },
          });
        });
      };

      const loadEnergyAnalysisSystemUsagePlanBinsTable = () => {
        if (!store.activeEnergyAnalysis()) return;

        return new Promise<{ [key: string]: number }[]>((resolve, reject) => {
          spinnerService.showMainSpinner();
          energyAnalysesService.getEnergyAnalysisSystemUsagePlanBinsTable(store.activeEnergyAnalysis()!.id).subscribe({
            next: response => {
              spinnerService.hideMainSpinner();
              patchState(store, {
                systemUsagePlanBinsColumnsDefinitions: response.columnsDefinitions || [],
                systemUsagePlanBinsData: response.rows,
              });
              resolve(response.rows);
            },
            error: error => {
              console.error('Error loading system usage plan bins table', error);
              spinnerService.hideMainSpinner();
              reject(error);
            },
          });
        });
      };

      const updateEnergyAnalysis = (
        energyAnalysisId: string,
        createEnergyAnalysisRequest: UpdateEnergyAnalysisRequest
      ) => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.updateEnergyAnalysis(energyAnalysisId, createEnergyAnalysisRequest).subscribe({
            next: response => {
              patchState(store, {
                activeEnergyAnalysis: response as EnergyAnalysis,
              });
              resolve();
            },
            error: error => {
              console.error('Error updating energy analysis', error);
              reject(error);
            },
          });
        });
      };

      const setEconomicParameters = (economicParameters: UpdateEnergyAnalysisRequest) => {
        patchState(store, {
          economicParameters: economicParameters,
        });
      };

      const setAllowSaveEconomicParameters = (allow: boolean) => {
        patchState(store, {
          allowSaveEconomicParameters: allow,
        });
      };

      const setRowsManualBinsTable = (rows: { [key: string]: number }[]) => {
        patchState(store, {
          rowsManualBinsTable: rows,
        });
      };

      const setAllowSaveManualBinsTable = (allow: boolean) => {
        patchState(store, {
          allowSaveManualBinsTable: allow,
        });
      };

      const loadSysthemUsagePlanGrid = () => {
        spinnerService.showMainSpinner();
        if (!store.activeEnergyAnalysis()) return;

        energyAnalysesService.getSystemUsagePlanGrid(store.activeEnergyAnalysis()!.id).subscribe({
          next: response => {
            spinnerService.hideMainSpinner();

            patchState(store, {
              systemUsagePlanGrid: response.data,
            });
          },
          error: error => {
            console.error('Error loading system usage plan grid', error);
          },
        });
      };
      const loadHeatingCapacityCurve = () => {
        return new Promise<{
          heatingCurve: { [key: number]: number };
          labels: CapacityCurveLabel;
        }>((resolve, reject) => {
          if (!store.activeEnergyAnalysis()) return;

          energyAnalysesService.getEnergyAnalysisHeatingCapacityCurve(store.activeEnergyAnalysis()!.id).subscribe({
            next: response => {
              if (response) {
                resolve({
                  heatingCurve: response?.data || {},
                  labels: {
                    keyLabel: response.keyLabel,
                    keyUom: response.keyUom,
                    valueLabel: response.valueLabel,
                    valueUom: response.valueUom,
                  },
                });
              }
            },
            error: error => {
              console.error('Error loading heating capacity curve', error);
              reject(error);
            },
          });
        });
      };

      const loadCoolingCapacityCurve = () => {
        return new Promise<{
          coolingCurve: {
            [key: number]: number;
          };
          labels: CapacityCurveLabel;
        }>((resolve, reject) => {
          if (!store.activeEnergyAnalysis()) return;

          energyAnalysesService.getEnergyAnalysisCoolingCapacityCurve(store.activeEnergyAnalysis()!.id).subscribe({
            next: response => {
              if (response) {
                resolve({
                  coolingCurve: response?.data || {},
                  labels: {
                    keyLabel: response.keyLabel,
                    keyUom: response.keyUom,
                    valueLabel: response.valueLabel,
                    valueUom: response.valueUom,
                  },
                });
              }
            },
            error: error => {
              console.error('Error loading cooling capacity curve', error);
              reject(error);
            },
          });
        });
      };
      const updateCoolingCapacityCurve = (updateCapacityCurve: { key: number; value: number }) => {
        return new Promise<{
          coolingCurve: { [key: number]: number };
        }>((resolve, reject) => {
          if (!store.activeEnergyAnalysis()) return;
          energyAnalysesService
            .updateEnergyAnalysisCoolingCapacityCurve(store.activeEnergyAnalysis()!.id, updateCapacityCurve)
            .subscribe({
              next: response => {
                resolve({
                  coolingCurve: response.data,
                });
              },
              error: error => {
                console.error('Error loading cooling capacity curve', error);
                reject(error);
              },
            });
        });
      };

      const updateManualBinsTable = (updateManualBinsTable: UpdateManualBinsTable[]) => {
        return new Promise<{ systemUsagePlanBinsData: { [key: string]: number }[] }>((resolve, reject) => {
          spinnerService.showMainSpinner();

          if (!store.activeEnergyAnalysis()) return;
          energyAnalysesService
            .updateManualClimateDataBinsTable(store.activeEnergyAnalysis()!.id, updateManualBinsTable)
            .subscribe({
              next: response => {
                spinnerService.hideMainSpinner();
                patchState(store, {
                  systemUsagePlanBinsData: response.content.rows,
                });

                resolve({
                  systemUsagePlanBinsData: response.content.rows,
                });
              },
              error: error => {
                spinnerService.hideMainSpinner();
                console.error('Error loading cooling capacity curve', error);
                reject(error);
              },
            });
        });
      };

      const updateHeatingCapacityCurve = (updateCapacityCurve: { key: number; value: number }) => {
        return new Promise<{
          heatingCurve: { [key: number]: number };
        }>((resolve, reject) => {
          if (!store.activeEnergyAnalysis()) return;
          energyAnalysesService
            .updateEnergyAnalysisHeatingCapacityCurve(store.activeEnergyAnalysis()!.id, updateCapacityCurve)
            .subscribe({
              next: response => {
                resolve({
                  heatingCurve: response.data,
                });
              },
              error: error => {
                console.error('Error loading heating capacity curve', error);
                reject(error);
              },
            });
        });
      };

      const loadEnergyAnalysisRules = () => {
        if (store.energyAnalysisRulesRequestParameters()) {
          energyAnalysesService.searchRules(store.energyAnalysisRulesRequestParameters()!).subscribe({
            next: response => {
              patchState(store, {
                rules: response.elements,
              });
            },
            error: error => {
              console.error('Error loading energy analyses list', error);
            },
          });
        }
      };
      const loadPresets = () => {
        energyAnalysesService.searchSystemUsagePlanPreset(store.presetRequestParameters()).subscribe({
          next: response => {
            patchState(store, {
              presets: response.elements,
            });
          },
          error: error => {
            console.error('Error loading cities', error);
          },
        });
      };

      const onlyDeletePreset = (presetId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.deleteSystemUsagePlanPreset(presetId).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error deleting preset', error);
              reject(error);
            },
          });
        });
      };

      const loadPreset = (presetId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          if (!store.activeEnergyAnalysis()) return;
          energyAnalysesService.copyFromPreset(store.activeEnergyAnalysis()!.id, presetId).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error loading preset', error);
              reject(error);
            },
          });
        });
      };

      const createPreset = (presetName: string): Promise<void> => {
        const prameters = {
          name: presetName,
          rules: store.rules(),
        };
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.createSystemUsagePlanPreset(prameters).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error creating preset', error);
              reject(error);
            },
          });
        });
      };

      const loadSystemUsagePlanHistogram = (): void => {
        if (!store.activeEnergyAnalysis()) return;
        energyAnalysesService.getSystemUsagePlanHistogram(store.activeEnergyAnalysis()!.id).subscribe({
          next: response => {
            const systemUsagePlan: SystemUsagePlanHistogramData[] = response.bins.map((item: any) => ({
              temperature: item.key,
              cooling: item.value.COOLING,
              heating: item.value.HEATING,
              simultaneous: item.value.COOLING_AND_HEATING,
            }));

            // Sort systemUsagePlan by temperature in ascending order
            const sortedSystemUsagePlan = [...systemUsagePlan].sort((a, b) => a.temperature - b.temperature);

            patchState(store, {
              systemUsagePlanHistogram: sortedSystemUsagePlan,
            });
          },
          error: error => {
            console.error('Error loading system usage plan histogram', error);
          },
        });
      };

      const loadSystems = () => {
        return new Promise<void>((resolve, reject) => {
          spinnerService.showMainSpinner();

          if (store.systemRequestParameters()) {
            energyAnalysesService.searchSystem(store.systemRequestParameters()!).subscribe({
              next: response => {
                spinnerService.hideMainSpinner();
                patchState(store, {
                  systems: response,
                });
                resolve();
              },
              error: error => {
                console.error('Error loading cities', error);
              },
            });
          }
        });
      };

      const getEnergyAnalysisSystem = (systemId: string) => {
        return new Promise<void>((resolve, reject) => {
          spinnerService.showMainSpinner();

          energyAnalysesService.getSystem(systemId).subscribe({
            next: response => {
              spinnerService.hideMainSpinner();
              patchState(store, {
                activeSystem: response as System,
                systems: {
                  elements: [response as System],
                  totalElements: 1,
                  totalPages: 1,
                },
              });
              resolve();
            },
            error: error => {
              console.error('Error loading energy analyses list', error);
              reject(error);
            },
          });
        });
      };

      const updateSystem = (systeId: string, updateSystem: UpdateSystemRequest): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.updateSystem(systeId, updateSystem).subscribe({
            next: response => {
              patchState(store, {
                activeSystem: response as System,
              });
              resolve();
            },
            error: error => {
              console.error('Error deleting system unit', error);
              reject(error);
            },
          });
        });
      };

      const deleteSystem = (systemId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.deleteSystem(systemId).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error deleting system', error);
              reject(error);
            },
          });
        });
      };

      const searchSystemUnits = (): void => {
        energyAnalysesService.searchSystemUnit(store.systemUnitRequestParameters()!).subscribe({
          next: response => {
            patchState(store, {
              systemUnits: response.elements,
              totalSystemUnits: response.totalElements,
            });
          },
          error: error => {
            console.error('Error loading system units', error);
          },
        });
      };

      const getEnergyAnalysisSystemUnit = (systemUnitId: string) => {
        return new Promise<void>((resolve, reject) => {
          spinnerService.showMainSpinner();

          energyAnalysesService.getSystemUnit(systemUnitId).subscribe({
            next: response => {
              spinnerService.hideMainSpinner();
              patchState(store, {
                activeSystemUnit: response as SystemUnit,
              });
              resolve();
            },
            error: error => {
              console.error('Error loading system unit', error);
              reject(error);
            },
          });
        });
      };
      const deleteSystemUnit = (unitId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.deleteSystemUnit(unitId).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error deleting system unit', error);
              reject(error);
            },
          });
        });
      };

      const updateSystemUnit = (unitId: string, updateSystemUnit: UpdateSystemUnitRequest): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.updateSystemUnit(unitId, updateSystemUnit).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error deleting system unit', error);
              reject(error);
            },
          });
        });
      };

      const getColumnsForSystemTaskPerformance = (system: System, hasBoiler: boolean): string[] => {
        let fieldsids: string[];
        switch (store.activeEnergyAnalysis()?.systemUsageType.id) {
          // cold
          case '1':
            if (system?.unitsFlags?.hasChillerEFC) {
              fieldsids = ['110029', '110031', '110043', '110035', '110036', '110056', '110062'];
            } else if (system?.unitsFlags?.hasChillerFC) {
              fieldsids = ['110031', '110043', '110035', '110036', '110056', '110062'];
            } else {
              fieldsids = ['110031', '110035', '110036', '110056', '110062'];
            }
            break;
          // hot
          case '2':
            if (hasBoiler) {
              fieldsids = ['110038', '110035', '110036', '110046', '110047', '110059', '110060', '110050', '110063'];
            } else {
              fieldsids = ['110038', '110035', '110036', '110059', '110063'];
            }
            break;
          // cold+hot
          case '3':
            fieldsids = ['110031'];
            if (system?.unitsFlags?.hasChillerFC) {
              fieldsids.push('110043');
            }
            if (hasBoiler) {
              fieldsids.push(
                '110038',
                '110035',
                '110036',
                '110046',
                '110047',
                '110057',
                '110058',
                '110040',
                '110050',
                '110062',
                '110063'
              );
            } else {
              fieldsids.push('110038', '110035', '110036', '110057', '110062', '110063');
            }
            break;
          default:
            fieldsids = [];
        }
        return fieldsids;
      };

      const getSystemsTaskPerformance = (systemIds: string[]): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          spinnerService.showMainSpinner();

          energyAnalysesService.getLatestSystemsEnergyAnalysisTasks(systemIds).subscribe({
            next: response => {
              resolve();
              // tasks performance
              const energyAnalysesTasks = response.systemEnergyAnalysisTasks;
              // check if at least one system has boiler and efc
              let hasBoiler = false;
              let hasEFC = false;

              if (energyAnalysesTasks) {
                for (let i = 0; i < energyAnalysesTasks.length; i++) {
                  const item = energyAnalysesTasks[i];
                  const system = store.systems().elements.find(sys => sys.id === item.systemId);
                  if (system?.unitsFlags?.hasBoiler) {
                    hasBoiler = true;
                  }
                  if (system?.unitsFlags?.hasChillerEFC) {
                    hasEFC = true;
                  }
                }
              }

              if (energyAnalysesTasks && energyAnalysesTasks.length > 0) {
                // colDef
                const performanceColDef: any[] = [];
                const binFields = [110028];
                const heatingCapacityField = {
                  dataType: 1,
                  descriptionId: 451708,
                  elcaDescriptionId: 1,
                  id: 110006,
                  unitOfMeasurement: 'KW',
                  unitOfMeasurementGroupId: 1,
                };
                const coolingCapacityField = {
                  dataType: 1,
                  descriptionId: 451711,
                  elcaDescriptionId: 1,
                  id: 110005,
                  unitOfMeasurement: 'KW',
                  unitOfMeasurementGroupId: 1,
                };
                if (store.activeEnergyAnalysis()?.systemUsageType.id === '1') {
                  if (hasEFC) {
                    binFields.push(110029, 110030, 110005, 110032);
                  } else {
                    binFields.push(110030, 110005, 110032);
                  }
                  if (energyAnalysesTasks[0]?.systemSvcResult?.fields) {
                    energyAnalysesTasks[0].systemSvcResult.fields['110005'] = coolingCapacityField;
                  }
                } else if (store.activeEnergyAnalysis()?.systemUsageType.id === '2') {
                  binFields.push(110030, 110006, 110034);
                  if (energyAnalysesTasks[0]?.systemSvcResult?.fields) {
                    energyAnalysesTasks[0].systemSvcResult.fields['110006'] = heatingCapacityField;
                  }
                } else if (store.activeEnergyAnalysis()?.systemUsageType.id === '3') {
                  if (hasEFC) {
                    binFields.push(110029, 110030, 110005, 110006, 110032, 110034);
                  } else {
                    binFields.push(110030, 110005, 110006, 110032, 110034);
                  }

                  if (energyAnalysesTasks[0]?.systemSvcResult?.fields) {
                    energyAnalysesTasks[0].systemSvcResult.fields['110006'] = heatingCapacityField;
                    energyAnalysesTasks[0].systemSvcResult.fields['110005'] = coolingCapacityField;
                  }
                }

                const colors = store.colors();

                if (energyAnalysesTasks[0]?.systemSvcResult?.fields) {
                  // binFieldsColDef
                  const binFieldsColDef: ColDef[] = [];
                  binFields.forEach(fieldId => {
                    if (energyAnalysesTasks[0].systemSvcResult?.fields?.[fieldId]) {
                      binFieldsColDef.push({
                        field: fieldId.toString(),
                        type: [ApmGridColumnType.WHITE_BACKGROUND],
                        headerClass: 'ag-header-label-centered text-uppercase justify-content-center',
                        headerName: `${resourcesService.getTextResource(energyAnalysesTasks[0].systemSvcResult.fields[fieldId].descriptionId)} (${energyAnalysesTasks[0].systemSvcResult.fields[fieldId].unitOfMeasurement})`,
                        headerTooltip: resourcesService.getTextResource(
                          energyAnalysesTasks[0].systemSvcResult.fields[fieldId].descriptionId
                        ),
                        valueFormatter: (params: ValueFormatterParams) => {
                          return (Math.round(params.value * 10000) / 10000).toString();
                        },
                        pinned: 'left',
                      });
                    }
                  });
                  performanceColDef.push({
                    field: 'bin',
                    type: [ApmGridColumnType.WHITE_BACKGROUND],
                    headerClass: 'ag-header-label-centered text-uppercase justify-content-center',
                    headerTooltip: resourcesService.getTextResource('448871'),
                    headerName: resourcesService.getTextResource('448871'),
                    children: binFieldsColDef,
                  });
                }
                const svcRequests: any[] = [];

                energyAnalysesTasks.forEach((item, index) => {
                  const system = store.systems().elements.find(sys => sys.id === item.systemId);
                  const cols = getColumnsForSystemTaskPerformance(system!, hasBoiler);
                  let systemColDefs: ColDef[] = [];

                  svcRequests.push({
                    svcRequest: item.svcRequest,
                    systemId: item.systemName,
                  });

                  if (item.systemSvcResult?.fields) {
                    for (const key of cols) {
                      const value = item.systemSvcResult.fields[Number(key)];
                      if (!binFields.includes(Number(key)) && value) {
                        systemColDefs.push({
                          field: `${key}_${item.systemId}`,
                          type: [ApmGridColumnType.WHITE_BACKGROUND],
                          headerClass: 'ag-header-label-centered text-uppercase justify-content-center',
                          headerName: `${resourcesService.getTextResource(value.descriptionId)} (${value.unitOfMeasurement})`,
                          headerTooltip: resourcesService.getTextResource(value.descriptionId),
                          valueFormatter: (params: ValueFormatterParams) => {
                            return (Math.round(params.value * 10000) / 10000).toString();
                          },
                          cellStyle: param => {
                            let opacity = 0.3;
                            if ((key === '110062' || key === '110063') && param.value > 0) {
                              opacity = 0.7;
                            }
                            return colors[index]
                              ? {
                                  'background-color': getColorWithOpacity(colors[index], opacity),
                                  'text-align': 'center',
                                }
                              : null;
                          },
                        });
                      }
                    }
                  }
                  performanceColDef.push({
                    field: item.systemName,
                    type: [ApmGridColumnType.WHITE_BACKGROUND],
                    headerClass: 'ag-header-label-centered text-uppercase justify-content-center',
                    headerTooltip: item.systemName,
                    headerName: item.systemName,
                    children: systemColDefs,
                  });
                });

                const dataMap = new Map<number, PerformanceData>();

                // totals
                let totalsFields: {
                  field: number;
                  idResource: number;
                }[] = [];
                const systemsTotals: any[] = [];
                totalsFields = [
                  {
                    field: 1000000000,
                    idResource: 448879,
                  },
                  {
                    field: 1000000001,
                    idResource: 448880,
                  },
                ];
                if (hasBoiler) {
                  totalsFields.push({
                    field: 1000000002,
                    idResource: 451703,
                  });
                }
                patchState(store, {
                  systemsTotalsFields: totalsFields,
                });

                energyAnalysesTasks.forEach((systemItem, index) => {
                  let systemBins = systemItem.svcRequest?.analysisInput?.groups?.find(
                    system => system.groupUID === systemItem.systemId
                  ).bin;

                  systemItem.systemSvcResult?.bins?.forEach(binItem => {
                    const requestBin = systemBins?.find((b: any) => b.binuid == binItem.id);

                    const heatingCapacity = requestBin.workingConditions.find((wc: any) => wc.id === 110006);
                    const coolingCapacity = requestBin.workingConditions.find((wc: any) => wc.id === 110005);

                    const binKey = binItem.values[110028]?.value;
                    if (binKey === undefined) return;

                    // Ottieni o crea la riga per questo bin
                    let rowData = dataMap.get(binKey);

                    if (!rowData) {
                      rowData = {};
                      dataMap.set(binKey, rowData);
                    }

                    // add heating capacity value if exists
                    rowData[110006] = heatingCapacity ? heatingCapacity.value : 0;
                    // add cooling capacity value if exists
                    rowData[110005] = coolingCapacity ? coolingCapacity.value : 0;

                    // Aggiungi tutti i valori
                    Object.entries(binItem.values).forEach(([key, val]) => {
                      if (!val) return;

                      const numKey = Number(key);
                      if (binFields.includes(numKey)) {
                        // Campo condiviso (solo se non esiste già)
                        if (!(key in rowData!)) {
                          rowData![key] = val.value;
                        }
                      } else {
                        // Campo specifico del sistema
                        rowData![`${key}_${systemItem.systemId}`] = val.value;
                      }
                    });
                  });
                  const totalData = systemItem.systemSvcResult?.fieldsTotals?.[totalsFields[0].field];
                  const averageData = systemItem.systemSvcResult?.fieldsAverages?.[totalsFields[1].field];
                  if (hasBoiler) {
                    const annualPEATotal = systemItem.systemSvcResult?.fieldsTotals?.[1000000002]?.quantity.value;
                    systemsTotals.push({
                      title: systemItem?.systemName || '',
                      [totalsFields[0].field]: totalData ? totalData.quantity.value : '-',
                      [totalsFields[1].field]: averageData ? averageData.quantity.value : '-',
                      [1000000002]: annualPEATotal,
                      color: colors[index] || undefined,
                    });
                  } else {
                    systemsTotals.push({
                      title: systemItem?.systemName || '',
                      [totalsFields[0].field]: totalData ? totalData.quantity.value : '-',
                      [totalsFields[1].field]: averageData ? averageData.quantity.value : '-',
                      color: colors[index] || undefined,
                    });
                  }
                });

                // Converti la Map in array e ordina
                const data: PerformanceData[] = Array.from(dataMap.values()).sort(
                  (a, b) => Number(a[110028]) - Number(b[110028])
                );

                patchState(store, {
                  taskPerformanceColDef: performanceColDef,
                  tasksPerformanceData: data,
                  systemsTotals,
                  svcRequests,
                });

                // for every curves
                const curves: {
                  systemName: string;
                  curves: any[];
                }[] = [];
                energyAnalysesTasks.forEach(item => {
                  if (item.systemSvcResult?.curves) {
                    curves.push({
                      systemName: item.systemName || '',
                      curves: item.systemSvcResult.curves,
                    });
                  }
                });
                patchState(store, {
                  systemSvcResultCurves: curves,
                });
                // use only the first for chart fields
                if (energyAnalysesTasks[0]) {
                  if (energyAnalysesTasks[0].systemSvcResult?.fields) {
                    patchState(store, {
                      taskPerformanceFields: energyAnalysesTasks[0].systemSvcResult.fields,
                    });
                  }
                }
              } else {
                patchState(store, {
                  taskPerformanceColDef: [],
                  tasksPerformanceData: [],
                  systemsTotals: [],
                  svcRequests: [],
                  systemSvcResultCurves: [],
                  taskPerformanceFields: {},
                });
              }

              // economic analyses
              const economicAnalyses = response.economicAnalysis;
              if (economicAnalyses) {
                const fields: {
                  valueYField: string;
                  valueXField: string;
                  label?: string;
                  color?: string;
                  fills?: boolean;
                  hideLegend?: boolean;
                  bulletType?: string;
                  bulletRadius?: number;
                }[] = [];
                const data: { [key: string]: number | string | null }[] = [];
                let info: PaybackInfo | null = {};
                const paybackDataset = economicAnalyses.result?.datasetsGroups?.find(
                  group => group.id === 'payback-summary'
                );
                info.tabLabel = paybackDataset?.name || '';

                paybackDataset?.datasets?.forEach((dataset, index) => {
                  if (dataset.type === 'NUMERIC_CURVE') {
                    fields.push({
                      valueYField: dataset.id,
                      valueXField: 'categoryX',
                      label: dataset.name,
                      color: store.colors()[index],
                      hideLegend: false,
                    });

                    info.keyLabel = resourcesService.getTextResource(dataset.keyLabel);
                    info.keyUnit = dataset.keyUom;
                    info.valueLabel = resourcesService.getTextResource(dataset.valueLabel);
                    info.valueUnit = dataset.valueUom;
                  } else if (dataset.type === 'POINT_2D') {
                    fields.push({
                      valueYField: dataset.name,
                      valueXField: 'categoryX',
                      color: '#ff0000',
                      bulletType: 'diamond',
                      bulletRadius: 4,
                      hideLegend: true,
                    });
                  }
                  if (dataset.data) {
                    Object.entries(dataset?.data).forEach(([key, val]) => {
                      if (data.find(item => item['categoryX'] == parseFloat(key))) {
                        const existingData = data.find(item => item['categoryX'] === parseFloat(key));
                        if (existingData) {
                          existingData[dataset.id] = val as number;
                        }
                      } else {
                        data.push({
                          categoryX: parseFloat(key),
                          [dataset.id]: val as number,
                        });
                      }
                    });
                  } else if (dataset.x && dataset.y) {
                    data.push({
                      categoryX: dataset.x,
                      [dataset.name]: dataset.y,
                      xLabel: dataset.xlabel || '',
                      yLabel: dataset.ylabel || '',
                    });
                  }
                  patchState(store, {
                    energyAnalysisPaybackFields: fields,
                    energyAnalysisPaybackData: data,
                    energyAnalysisPaybackInfo: info,
                  });
                });

                const gropComparisonFields = economicAnalyses.result?.datasetsGroups?.find(
                  group => group.id === 'systems-comparison-section'
                );
                const comparisonInfo: GroupComparisonInfo = {
                  title: gropComparisonFields?.name || '',
                };

                const chartData: { [key: string]: number | string | null }[] = [];

                const columnDesc: {
                  valueYField: string;
                  categoryXField: string;
                  label?: string;
                  color?: string;
                }[] = [];
                gropComparisonFields?.datasets.forEach((dataset, index) => {
                  if (dataset.type === 'TABLE') {
                    comparisonInfo.columnsDef = dataset.columnsDefinitions;
                    comparisonInfo.columnsRows = dataset.rows?.map(row => {
                      return {
                        ...row,
                      };
                    });
                  }
                  if (dataset.type === 'HISTOGRAM') {
                    columnDesc.push({
                      valueYField: dataset.id,
                      categoryXField: 'categoryX',
                      label: dataset.name,
                      color: store.colors()[columnDesc.length],
                    });
                    comparisonInfo.chartLabels = {
                      keyLabel: resourcesService.getTextResource(dataset.keyLabel),
                      keyUnit: dataset.keyUom,
                      valueLabel: resourcesService.getTextResource(dataset.valueLabel),
                      valueUnit: dataset.valueUom,
                    };
                  }
                  dataset.bins?.forEach(bin => {
                    let valueKey = 'discounted_cost_per_year';
                    Object.keys(bin.value).forEach(key => {
                      valueKey = key; // to avoid ts error
                    });
                    if (chartData.find(item => item['categoryX'] == bin.key)) {
                      const existedBin = chartData.find(item => item['categoryX'] == bin.key);
                      if (existedBin) {
                        existedBin[dataset.id] = bin.value[valueKey];
                      }
                    } else {
                      chartData.push({
                        categoryX: bin.key,
                        // inserire dinamicamente il campo. è sempre solo uno?
                        [dataset.id]: bin.value[valueKey],
                      });
                    }
                  });
                });
                patchState(store, {
                  energyAnalysisGroupComparisonInfo: comparisonInfo,
                  energyAnalysisGroupComparisonData: chartData,
                  energyAnalysisGroupComparisonColumns: columnDesc,
                });
              } else {
                patchState(store, {
                  energyAnalysisPaybackData: [],
                  energyAnalysisPaybackFields: [],
                  energyAnalysisPaybackInfo: null,
                  energyAnalysisGroupComparisonData: [],
                  energyAnalysisGroupComparisonColumns: [],
                  energyAnalysisGroupComparisonInfo: null,
                });
              }
              spinnerService.hideMainSpinner();
            },
            error: error => {
              console.error('Error deleting system unit', error);
              reject(error);
              spinnerService.hideMainSpinner();
            },
          });
        });
      };
      const getSystemWorkingModes = (): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.getSystemWorkingMode().subscribe({
            next: response => {
              resolve();
              patchState(store, {
                systemWorkingModes: response,
              });
            },
            error: error => {
              console.error('Error deleting system unit', error);
              reject(error);
            },
          });
        });
      };

      const loadSystemsComparisonSettings = (energyAnalysisId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.getSystemComparisonSettings(energyAnalysisId).subscribe({
            next: response => {
              resolve();
              patchState(store, {
                systemsComparisonSettings: response.systemsComparisonSectionSettings,
              });
            },
            error: error => {
              console.error('Error loading systems comparison settings', error);
              reject(error);
            },
          });
        });
      };

      const updateSystemsComparisonSettings = (
        energyAnalysisId: string,
        updateSettings: UpdateSystemsComparisonSettingsRequest
      ): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.updateSystemComparisonSettings(energyAnalysisId, updateSettings).subscribe({
            next: response => {
              resolve();
              patchState(store, {
                systemsComparisonSettings: response.systemsComparisonSectionSettings,
              });
            },
            error: error => {
              console.error('Error updating systems comparison settings', error);
              reject(error);
            },
          });
        });
      };

      const cleanActiveEnergyAnalysis = () => {
        patchState(store, {
          activeEnergyAnalysis: null,
          activeSystem: null,
          activeSystemUnit: null,
          systemUnits: [],
          totalSystemUnits: 0,
          systemUsagePlanGrid: [],
          systemUsagePlanHistogram: [],
          systemUsagePlanBinsData: [],
          capacityCurveData: [],
          cityClimateData: [],
          activeTaskPerformanceCol: [],
          tasksPerformanceData: [],
          energyAnalysisPaybackData: [],
          energyAnalysisPaybackFields: [],
          energyAnalysisPaybackInfo: null,
          systemsTotals: [],
          systemsTotalsFields: [],
          systemSvcResultCurves: [],
          taskPerformanceFields: null,
          activeTaskStatus: null,
        });
      };
      const updateactiveTaskStatus = (task: Task) => {
        patchState(store, {
          activeTaskStatus: task,
        });
      };

      const cleanActiveSystemUnit = () => {
        patchState(store, {
          activeSystemUnit: null,
        });
      };

      const cleanTasksPerformanceData = () => {
        patchState(store, {
          tasksPerformanceData: [],
          energyAnalysisPaybackData: [],
          energyAnalysisPaybackFields: [],
          energyAnalysisPaybackInfo: null,
          systemsTotals: [],
          systemSvcResultCurves: [],
          taskPerformanceFields: null,
          activeTaskStatus: null,
        });
      };

      return {
        loadEnergyAnalyses,
        updateEnergyAnalysisFilters,
        getEnergyAnalysis: (energyAnalysisId: string) => {
          getEnergyAnalysis(energyAnalysisId).then(() => {
            getEnergyAnalysisConfig(energyAnalysisId);
          });
        },
        getEnergyAnalysisConfig,
        updateEnergyAnalysis,
        setEconomicParameters,
        setAllowSaveEconomicParameters,
        setRowsManualBinsTable,
        setAllowSaveManualBinsTable,
        updateManualBinsTableAndRefresh: (updateManualBinsTableData: UpdateManualBinsTable[]) => {
          updateManualBinsTable(updateManualBinsTableData).then(response => {
            setRowsManualBinsTable(response.systemUsagePlanBinsData);
            setAllowSaveManualBinsTable(false);
            loadSystemUsagePlanHistogram();
          });
        },

        loadEnergyAnalysisAndSystems: async (energyAnalysisId: string): Promise<void> => {
          await getEnergyAnalysis(energyAnalysisId);
          await Promise.all([loadSystems(), getEnergyAnalysisConfig(energyAnalysisId)]);
        },
        getEnergyAnalysisSystem,
        loadEnergyAnalysisSystemAndUnits: (systemId: string) => {
          getEnergyAnalysisSystem(systemId).then(() => {
            searchSystemUnits();
          });
        },
        getEnergyAnalysisSystemUnit,
        loadPresets,
        createPresetAndRefresh: (presetName: string) => {
          createPreset(presetName).then(() => {
            loadPresets();
          });
        },
        deletePresetAndRefresh: (presetId: string) => {
          onlyDeletePreset(presetId).then(() => {
            loadPresets();
          });
        },
        loadPresetAndRefreshRulesAndDataGrid: (presetId: string) => {
          loadPreset(presetId).then(async () => {
            loadSysthemUsagePlanGrid();
            loadEnergyAnalysisSystemUsagePlanBinsTable();
            loadEnergyAnalysisRules();
            loadSystemUsagePlanHistogram();
            // reload capacity curve
            const { coolingCurve, labels: coolingLabels } = await loadCoolingCapacityCurve();
            const { heatingCurve, labels: heatingLabels } = await loadHeatingCapacityCurve();
            let dataChart: CapacityCurveData[] = [];
            for (const [key, value] of Object.entries(coolingCurve)) {
              dataChart.push({
                temperature: parseInt(key),
                coolingPower: value * 100,
                thermalPower: heatingCurve[key] * 100,
              });
            }
            const sortedDataChart = [...dataChart].sort((a, b) => a.temperature - b.temperature);

            patchState(store, {
              capacityCurveData: sortedDataChart,
              capacityCurveDataLabels: {
                keyLabel: coolingLabels.keyLabel || heatingLabels.keyLabel || '',
                keyUom: coolingLabels.keyUom || heatingLabels.keyUom || '',
                valueLabel: coolingLabels.valueLabel || heatingLabels.valueLabel || '',
                valueUom: coolingLabels.valueUom || heatingLabels.valueUom || '',
              },
            });
          });
        },
        loadDataRulesAndRefreshRulesAndCharts: () => {
          loadSysthemUsagePlanGrid();
          loadEnergyAnalysisSystemUsagePlanBinsTable();
          loadEnergyAnalysisRules();
          loadSystemUsagePlanHistogram();
        },
        loadCityClimateDataAndRefreshMainChartData: async (cityId: string) => {
          energyAnalysesService.getClimateData(cityId, store.cityClimateDataParams()).subscribe({
            next: async response => {
              if (!response) {
                return;
              }
              const storeCityClimateData: cityChartData[] = response.content.bins.map(item => ({
                temperature: item.key,
                hours: item.value.measurement_hours,
              }));
              const sortedCityClimateData = [...storeCityClimateData].sort((a, b) => a.temperature - b.temperature);

              patchState(store, {
                cityClimateData: sortedCityClimateData,
              });

              // Load capacity curve data and histogram
              if (store.activeEnergyAnalysis()) {
                loadSystemUsagePlanHistogram();
                loadEnergyAnalysisSystemUsagePlanBinsTable();
                const { coolingCurve, labels: coolingLabels } = await loadCoolingCapacityCurve();
                const { heatingCurve, labels: heatingLabels } = await loadHeatingCapacityCurve();
                let dataChart: CapacityCurveData[] = [];
                for (const [key, value] of Object.entries(coolingCurve)) {
                  dataChart.push({
                    temperature: parseInt(key),
                    coolingPower: value * 100,
                    thermalPower: heatingCurve[key] * 100,
                  });
                }
                const sortedDataChart = [...dataChart].sort((a, b) => a.temperature - b.temperature);
                let valueLabel = '';
                if (store.activeEnergyAnalysis()?.systemType.id !== '2') {
                  // add cooling
                  valueLabel = resourcesService.getTextResource(coolingLabels.valueLabel);
                }
                if (store.activeEnergyAnalysis()?.systemType.id !== '1') {
                  // add heating
                  if (valueLabel !== '') {
                    valueLabel += ' - ';
                  }
                  valueLabel += resourcesService.getTextResource(heatingLabels.valueLabel);
                }

                const valueUom = coolingLabels.valueUom || heatingLabels.valueUom || '';
                const keyLabel = coolingLabels.keyLabel || heatingLabels.keyLabel || '';
                const keyUom = coolingLabels.keyUom || heatingLabels.keyUom || '';

                patchState(store, {
                  capacityCurveData: sortedDataChart,
                  capacityCurveDataLabels: {
                    keyLabel,
                    keyUom,
                    valueLabel,
                    valueUom,
                  },
                });
              }
            },
            error: error => {
              console.error('Error loading city climate data', error);
            },
          });
        },
        loadCapacityCurve: async () => {
          const { coolingCurve, labels: coolingLabels } = await loadCoolingCapacityCurve();
          const { heatingCurve, labels: heatingLabels } = await loadHeatingCapacityCurve();
          let dataChart: CapacityCurveData[] = [];
          for (const [key, value] of Object.entries(coolingCurve)) {
            dataChart.push({
              temperature: parseInt(key),
              coolingPower: value * 100,
              thermalPower: heatingCurve[key] * 100,
            });
          }
          const sortedDataChart = [...dataChart].sort((a, b) => a.temperature - b.temperature);

          patchState(store, {
            capacityCurveData: sortedDataChart,
            capacityCurveDataLabels: {
              keyLabel: coolingLabels.keyLabel || heatingLabels.keyLabel || '',
              keyUom: coolingLabels.keyUom || heatingLabels.keyUom || '',
              valueLabel: coolingLabels.valueLabel || heatingLabels.valueLabel || '',
              valueUom: coolingLabels.valueUom || heatingLabels.valueUom || '',
            },
          });
        },
        updateCapacityCurveFromCooling: async (updateCapacityCurve: { key: number; value: number }) => {
          const { coolingCurve } = await updateCoolingCapacityCurve(updateCapacityCurve);
          const dataChart: CapacityCurveData[] = [];
          const existingData = store.capacityCurveData();

          for (const [key, value] of Object.entries(coolingCurve)) {
            const existingRow = existingData.find(item => item.temperature === parseInt(key));
            dataChart.push({
              temperature: parseInt(key),
              coolingPower: value * 100,
              thermalPower: existingRow?.thermalPower || 0,
            });
          }

          const sortedDataChart = [...dataChart].sort((a, b) => a.temperature - b.temperature);

          patchState(store, {
            capacityCurveData: [...sortedDataChart],
          });
        },
        updateCapacityCurveFromHeating: async (updateCapacityCurve: { key: number; value: number }) => {
          const { heatingCurve } = await updateHeatingCapacityCurve(updateCapacityCurve);
          const dataChart: CapacityCurveData[] = [];
          const existingData = store.capacityCurveData();

          for (const [key, value] of Object.entries(heatingCurve)) {
            const existingRow = existingData.find(item => item.temperature === parseInt(key));
            dataChart.push({
              temperature: parseInt(key),
              coolingPower: existingRow?.coolingPower || 0,
              thermalPower: value * 100,
            });
          }
          const sortedDataChart = [...dataChart].sort((a, b) => a.temperature - b.temperature);

          patchState(store, {
            capacityCurveData: [...sortedDataChart],
          });
        },
        loadSystemUsagePlanHistogram,
        loadEnergyAnalysisSystemUsagePlanBinsTable,
        deleteAndRefreshSystemUnit: (unitId: string) => {
          deleteSystemUnit(unitId).then(() => {
            getEnergyAnalysisSystem(store.activeSystem()!.id).then(() => {
              searchSystemUnits();
            });
          });
        },
        updateSystemUnitandRefresh: (unitId: string, updateSystemUnitRequest: UpdateSystemUnitRequest) => {
          updateSystemUnit(unitId, updateSystemUnitRequest).then(() => {
            getEnergyAnalysisSystem(store.activeSystem()!.id);
            searchSystemUnits();
          });
        },
        updateSystem,
        deleteAndRefreshSystem: (systemId: string) => {
          return new Promise<void>((resolve, reject) => {
            deleteSystem(systemId)
              .then(() => {
                loadSystems()
                  .then(() => {
                    resolve();
                  })
                  .catch(reject);
              })
              .catch(reject);
          });
        },
        getSystemTaskPerformance: (energyAnalysesSystemId: string) => {
          return getSystemsTaskPerformance([energyAnalysesSystemId]).then(() => {
            if (store.activeEnergyAnalysis()) {
              getEnergyAnalysis(store.activeEnergyAnalysis()!.id);
            }
          });
        },
        getSystemsTaskPerformance,
        updateactiveTaskStatus,
        cleanActiveEnergyAnalysis,
        cleanActiveSystemUnit,
        cleanTasksPerformanceData,
        getSystemWorkingModes,
        loadSystemsSettingsAndPerformances: (energyAnalisisId: string) => {
          loadSystemsComparisonSettings(energyAnalisisId).then(() => {
            getSystemsTaskPerformance(store.selectedSystemsIds());
          });
        },
        updateSystemsSettingsAndPerformances: (
          energyAnalisisId: string,
          updateSettings: UpdateSystemsComparisonSettingsRequest
        ) => {
          updateSystemsComparisonSettings(energyAnalisisId, updateSettings).then(() => {
            getSystemsTaskPerformance(store.selectedSystemsIds());
          });
        },
      };
    }
  )
);
